<template>
  <div
    id="app"
    :data-theme="getWCAGSettings.contrastMode ? 'contrast' : 'default'"
    :cursor-mode="getWCAGSettings.optionallyBiggerCursor ? 'bigger' : 'default'"
  >
    <!--mobile-suggester v-if="isVisible && !isDktUser"/-->
    <!--mobile-suggester-screen /-->
    <transition
      name="slide"
      enter-active-class="animated slideInDown faster"
      leave-active-class="animated slideOutUp faster"
    >
      <dropdown-notification v-if="notification" v-bind="notification" />
    </transition>
    <logged-in-layout v-if="isLoggedInLayoutVisible" />
    <router-view v-else />
    <transition
      mode="in-out"
      enter-active-class="animated fadeIn faster"
      leave-active-class="animated fadeOut faster"
    >
      <!--modal
        v-if="modalComponent"
        :component="modalComponent"
        :key="modalKey"
        :class="{'modalBlur': showSuggester}"
      ></modal-->
      <modal
        v-if="modalComponent"
        :component="modalComponent"
        :key="modalKey"
      ></modal>
    </transition>
    <android-smart-app-banner v-if="showSmartAppBanner" />
    <ios-smart-app-banner v-if="showSmartAppBanner" />
  </div>
</template>

<script>
import DeviceDetector from 'device-detector-js';
import store from '@/store';
import EventBus from '@/components/shared/eventbus';
import { mapActions, mapGetters } from 'vuex';
import Modal from '@/components/shared/mixins/modal';
import ValidMobileDeviceMixin from '@/components/shared/mixins/ValidMobileDeviceMixin';
import { fromShortToLong, availableLanguages } from '@/lang/languageCodes';
import i18n from '@/lang/lang';
import BasicMixin from '@/components/shared/mixins/BasicMixin';

const LoggedInLayout = () => import('@/screens/LoggedInLayout');
const DropdownNotification = () =>
  import('@/components/shared/modals/DropdownNotification');
const AndroidSmartAppBanner = () =>
  import('@/components/shared/elements/banners/AndroidSmartAppBanner');
const IosSmartAppBanner = () =>
  import('@/components/shared//elements/banners/IosSmartAppBanner');
const MobileSuggesterScreen = () =>
  import('@/screens/generals/MobileSuggesterScreen');

export default {
  name: 'App',
  components: {
    LoggedInLayout,
    DropdownNotification,
    AndroidSmartAppBanner,
    IosSmartAppBanner,
    'mobile-suggester-screen': MobileSuggesterScreen
  },
  mixins: [Modal, BasicMixin, ValidMobileDeviceMixin],
  data() {
    return {
      isPushSupported: false,
      notification: null,
      notificationTimeoutId: null,
      isVisible: false
      // showSuggester: false
    };
  },
  async created() {
    if (this.getWCAGSettings.optionallyBiggerCursor) {
      this.setOptionallyBiggerCursor(true);
    }
    if (this.getWCAGSettings.contrastMode) {
      this.setContrastMode(true);
    }
    this.loadCookieBot(false);
    this.registerModals(); // from modal.js mixin
    this.setDefaultSiteLanguage();
    if (store.getters.hasToken) {
      await this.getLoggedInUserProfile().then((loggedInUserProfile) => {
        if (loggedInUserProfile.data.settings) {
          this.setSiteLanguage(
            fromShortToLong[loggedInUserProfile.data.settings.language]
          );
        } else if (loggedInUserProfile.data.provider === 'idp') {
          this.setSettings({
            language: 'hu'
          });
          this.setSiteLanguage('hu_HU');
        }
      });
    } else if (
      this.getSiteLanguage &&
      availableLanguages.includes(this.getSiteLanguage)
    ) {
      i18n.locale = this.getSiteLanguage;
    } else if (
      availableLanguages.includes(navigator.language.substring(0, 2))
    ) {
      i18n.locale = fromShortToLong[navigator.language.substring(0, 2)];
    } else {
      i18n.locale = 'en_US';
    }
    // this.$eventBus.onShowSuggester(this, (payload) => {
    //   this.showSuggester = payload;
    // });
    this.$eventBus.onNotificationTriggered(this, (payload) => {
      this.notification = payload;
      if (payload.timeout) {
        this.notificationTimeout(payload.timeout);
      }
    });
    this.$eventBus.onCookieBotTriggered(this, (payload) => {
      this.loadCookieBot(payload);
    });
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
    window.addEventListener('keydown', this.handleFirstTab);
    this.initYbug();
  },
  mounted() {
    this.loadFacebookPixel();
    const headViewport = document.querySelector('head meta[name="viewport"]');
    if (this.isVisible) {
      const headViewportContentApp =
        'width=device-width, initial-scale=1.0, maximum-scale=1.0,user-scalable=no';
      headViewport.setAttribute('content', headViewportContentApp);
    } else {
      const headViewportContentApp =
        'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=5.0, user-scalable=yes';
      headViewport.setAttribute('content', headViewportContentApp);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.windowResizeEvent);
    this.$eventBus.offNotificationTriggered();
    this.$eventBus.offCookieBotTriggered();
  },
  watch: {
    $route(to, from) {
      this.setTabTitle();
      const withoutAuth = [
        'LandingScreen',
        'PricingScreen',
        'PurchaseScreen',
        'PrivacyPolicy',
        'TermsAndConditions'
      ];

      if (
        this.getGuestLink !== undefined &&
        ((to.meta.group === 'Exercises' && !to.params.classId) ||
          withoutAuth.includes(to.name))
      ) {
        this.setGuestLink(undefined);
      }
    },
    isVisible() {},
    getSiteLanguage() {
      this.setTabTitle();

      // Reinitialize Ybug
      if ('Ybug' in window) {
        window.Ybug.destroy();
        delete window.Ybug;
        delete window.ybug_settings;
        this.initYbug();
      }
    },
    getWCAGSettings: {
      deep: true,
      immediate: true,
      handler(val, oldval) {
        if (
          JSON.stringify(val) !== undefined &&
          JSON.stringify(oldval) !== undefined
        ) {
          const parsedVal = JSON.parse(JSON.stringify(val));
          const parsedOldVal = JSON.parse(JSON.stringify(oldval));
          if (
            parsedVal.optionallyBiggerCursor !==
            parsedOldVal.optionallyBiggerCursor
          ) {
            this.setOptionallyBiggerCursor(parsedVal.optionallyBiggerCursor);
          }
          if (parsedVal.contrastMode !== parsedOldVal.contrastMode) {
            this.setContrastMode(parsedVal.contrastMode);
          }
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      loggedInUserProfile: 'users/getLoggedInUserProfile',
      getSiteLanguage: 'settings/getSiteLanguage',
      getUserSettings: 'settings/getUserSettings',
      isDktUser: 'users/isDktUser',
      getGuestLink: 'settings/getGuestLink',
      appBannerVisible: 'settings/getAppBannerVisible',
      getForceWebViewStatus: 'settings/getForceWebViewStatus',
      getWCAGSettings: 'settings/getWCAGSettings'
    }),
    showSmartAppBanner() {
      return (
        !location.pathname.split('/')[1].includes('dkt') &&
        this.validDevice() &&
        this.appBannerVisible
      );
    },
    isMobile() {
      return window.innerWidth < 768;
    },
    isDktUser() {
      if (
        this.loggedInUserProfile &&
        this.loggedInUserProfile.provider === 'idp'
      ) {
        return true;
      }
      return false;
    },
    isLoggedInLayoutVisible() {
      return (
        ((!this.isRoleSelector && this.loggedInUserProfile) ||
          this.isExercises) &&
        !this.isPurchase &&
        !location.pathname.split('/')[1].includes('dkt')
      );
    },
    isRoleSelector() {
      return this.$route.path === '/users/registration/role';
    },
    isExercises() {
      return this.$route.path.includes('/exercises');
    },
    isPurchase() {
      return this.$route.path.includes('/purchase');
    }
  },
  methods: {
    ...mapActions({
      changedPushNotificationStatus: 'users/changedPushNotificationStatus',
      getLoggedInUserProfile: 'users/getLoggedInUserProfile',
      setSiteLanguage: 'settings/setSiteLanguage',
      setSettings: 'settings/setSettings',
      setGuestLink: 'settings/setGuestLink'
    }),
    createUserObjectForYBug() {
      const deviceDetector = new DeviceDetector();
      const userAgent = navigator.userAgent;
      const currentDevice = deviceDetector.parse(userAgent);
      const deviceInfo = {
        platform: '-',
        device: '-',
        client: '-',
        os: '-',
        network: '-',
        screen: {
          windowDimensions: {
            width: window && window.innerWidth ? window.innerWidth : '-',
            height: window && window.innerHeight ? window.innerHeight : '-'
          },
          screenResolution: {
            width: screen && screen.width ? screen.width : '-',
            height: screen && screen.height ? screen.height : '-'
          }
        }
      };
      if (navigator) {
        if (navigator.userAgentData) {
          deviceInfo.platform = navigator.userAgentData.platform;
        }
        if (navigator.connection) {
          deviceInfo.network = {
            effectiveType: navigator.connection.effectiveType, // "slow-2g", "2g", "3g", "4g" - a kapcsolat típusa
            downlink: navigator.connection.downlink, // szám - becsült sávszélesség Mbps-ben
            rtt: navigator.connection.rtt, // szám - Round-Trip Time milliszekundumban
            saveData: navigator.connection.saveData, // boolean - adatmegtakarítás mód be van-e kapcsolva
            type: navigator.connection.type, // "wifi", "cellular", "ethernet", "none", stb.
            downlinkMax: navigator.connection.downlinkMax // szám - a maximális sávszélesség Mbps-ben
          };
        }
      }
      if (currentDevice) {
        if (currentDevice.device) {
          deviceInfo.device = {
            type: currentDevice.device.type,
            brand: currentDevice.device.brand,
            model: currentDevice.device.model,
            identified: currentDevice.device.identified
          };
        }
        if (currentDevice.client) {
          deviceInfo.client = {
            type: currentDevice.client.type,
            name: currentDevice.client.name,
            version: currentDevice.client.version,
            engine: currentDevice.client.engine,
            engineVersion: currentDevice.client.engineVersion
          };
        }
        if (currentDevice.os) {
          deviceInfo.os = {
            name: currentDevice.os.name,
            version: currentDevice.os.version,
            platform: currentDevice.os.platform
          };
        }
      }

      let userInfo = '-';
      if (this.loggedInUserProfile) {
        userInfo = {
          id: this.loggedInUserProfile._id,
          limitations: this.loggedInUserProfile.limitations,
          settings: this.loggedInUserProfile.settings
        };
      }

      return {
        type: 'XEROPAN_CLASSROOM',
        user: userInfo,
        browserInfo: navigator && navigator.userAgent,
        device: deviceInfo
      };
    },
    initYbug() {
      const user = this.createUserObjectForYBug();
      window.ybug_settings = {
        id: process.env.VUE_APP_YBUG_ID,
        launcher_position: 'right-middle',
        language_override: document.documentElement.lang,
        nps: true,
        email_required: true,
        translate: {
          'feedbackForm.NpsLabel': JSON.stringify(
            this.$t('ybug.feedback.title')
          ),
          'feedbackForm.Nps.Unlikely': JSON.stringify(
            this.$t('ybug.feedback.dontLike')
          ),
          'feedbackForm.Nps.Likely': JSON.stringify(
            this.$t('ybug.feedback.like')
          )
        },
        user,
        beforesend() {
          const tmpUser = this.createUserObjectForYBug();
          const currentUserSettings = window.ybug_settings
            ? window.ybug_settings.user
            : {};
          if (window.ybug_settings) {
            window.ybug_settings.user = {
              ...currentUserSettings,
              ...tmpUser
            };
          }
        }
      };
      const ybug = document.createElement('script');
      ybug.type = 'text/javascript';
      ybug.async = true;
      ybug.src = `https://widget.ybug.io/button/${window.ybug_settings.id}.js`;
      const s = document.getElementsByTagName('script')[0];
      s.parentNode.insertBefore(ybug, s);
    },
    setContrastMode() {},
    setOptionallyBiggerCursor(val) {
      if (val) {
        document.body.classList.add('bigger-cursor');
      } else {
        document.body.classList.remove('bigger-cursor');
      }
    },
    handleFirstTab(e) {
      if (e.keyCode === 9 && !this.isVisible) {
        document.body.classList.add('user-is-tabbing');
        window.removeEventListener('keydown', this.handleFirstTab);
        window.addEventListener('mousedown', this.handleMouseDownOnce);
      }
    },
    handleMouseDownOnce() {
      document.body.classList.remove('user-is-tabbing');

      window.removeEventListener('mousedown', this.handleMouseDownOnce);
      window.addEventListener('keydown', this.handleFirstTab);
    },
    loadFacebookPixel() {
      if (!this.isDktUser) {
        const fbPixelScript = document.createElement('script');
        fbPixelScript.setAttribute('src', '/static/facebookPixel.js');
        document.head.appendChild(fbPixelScript);
      }
      // const fbPixelNoScript = document.createElement('img');
      // fbPixelNoScript.setAttribute('src', 'https://www.facebook.com/tr?id=3418500094895764&ev=PageView&noscript=1');
      // document.body.appendChild(fbPixelNoScript);
    },
    handleResize() {
      this.isVisible = window.innerWidth < 768;
      const headViewport = document.querySelector('head meta[name="viewport"]');
      if (this.isMobile) {
        const headViewportContentApp =
          'width=device-width, initial-scale=1.0, maximum-scale=1.0,user-scalable=no';
        headViewport.setAttribute('content', headViewportContentApp);
      } else {
        const headViewportContentApp =
          'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=5.0, user-scalable=yes';
        headViewport.setAttribute('content', headViewportContentApp);
      }
    },
    setDefaultSiteLanguage() {
      if (
        this.getSiteLanguage &&
        availableLanguages.includes(this.getSiteLanguage) &&
        this.$store.state.settings.siteLanguage !== undefined
      ) {
        this.setSiteLanguage(this.getSiteLanguage);
      } else if (
        this.getSiteLanguage &&
        !availableLanguages.includes(this.getSiteLanguage) &&
        this.$store.state.settings.siteLanguage !== undefined
      ) {
        this.setSiteLanguage(undefined);
      }
    },
    loadCookieBot(forced) {
      if (!forced) {
        if (
          process.env.NODE_ENV === 'development' ||
          this.isDktUser ||
          document.location.href.includes('classSummaryData') ||
          document.location.href.includes('summaryData') ||
          location.pathname.split('/')[1].includes('dkt')
        ) {
          return;
        }
      }
      const cookieBotScript = document.createElement('script');
      cookieBotScript.setAttribute(
        'src',
        'https://consent.cookiebot.com/uc.js'
      );
      cookieBotScript.setAttribute('id', 'Cookiebot');
      cookieBotScript.setAttribute('type', 'text/javascript');
      cookieBotScript.setAttribute('data-blockingmode', 'auto');
      cookieBotScript.setAttribute(
        'data-cbid',
        'ac0b1d40-58ed-4de7-9c00-46a2ce6792dd'
      );
      cookieBotScript.defer = true;
      document.body.appendChild(cookieBotScript);
    },
    notificationTimeout(timeout) {
      this.notificationTimeoutId = setTimeout(() => {
        this.deleteNotification();
      }, timeout);
    },
    deleteNotification() {
      this.$eventBus.$emit('NotificationExpired');
      this.notification = null;
    },
    setTabTitle() {
      if (
        i18n.locale !== this.getSiteLanguage &&
        this.getSiteLanguage !== undefined
      ) {
        i18n.locale = this.getSiteLanguage;
      } else if (this.getSiteLanguage === undefined) {
        i18n.locale = 'en_US';
      }
      let title = 'Xeropan Classroom';
      switch (this.$route.name) {
        case 'RegistrationScreen':
          if (this.$route.path.includes('registration')) {
            title = this.$t('app.welcome.registration.btnText');
          }
          if (this.$route.path.includes('registration/role')) {
            title = this.$t('app.welcome.roleSelector.title');
          }
          if (this.$route.path.includes('login')) {
            title = this.$t('app.page.title.login');
          }
          if (this.$route.path.includes('request-password-reset')) {
            title = this.$t('app.page.title.requestPassword');
          }
          break;
        case 'PasswordResetScreen':
          title = this.$t('app.page.title.resetPassword');
          break;
        case 'AccountSettingsScreen':
          title = this.$t('app.page.title.accountSettings');
          break;
        case 'ClassesScreen':
          title = this.$t('app.page.title.classes');
          break;
        case 'ClassScreen':
          if (this.$route.path.includes('overview')) {
            title = this.$t('app.page.title.classOverview');
          }
          if (this.$route.path.includes('assignments')) {
            title = this.$t('app.page.title.classAssignments');
          }
          if (this.$route.path.includes('students')) {
            title = this.$t('app.page.title.classStudents');
          }
          if (this.$route.path.includes('statistics')) {
            title = this.$t('app.page.title.classStatistics');
          }
          if (this.$route.path.includes('settings')) {
            title = this.$t('app.page.title.classSettings');
          }
          break;
        case 'ClassRankingScreen':
          title = this.$t('app.page.title.classRanking');
          break;
        case 'StudentScreen':
          if (this.$route.path.includes('overview')) {
            title = this.$t('app.page.title.studentOverview');
          }
          if (this.$route.path.includes('assignments')) {
            title = this.$t('app.page.title.studentAssignments');
          }
          if (this.$route.path.includes('activity')) {
            title = this.$t('app.page.title.studentActivity');
          }
          if (this.$route.path.includes('statistics')) {
            title = this.$t('app.page.title.studentStatistics');
          }
          break;
        case 'ExercisesScreen':
          if (this.$route.path.includes('search')) {
            title = this.$t('app.page.title.exerciseSearch');
          }
          if (this.$route.path.includes('curriculum')) {
            title = this.$t('app.page.title.exerciseCurriculum');
          }
          if (this.$route.path.includes('learn')) {
            title = this.$t('app.page.title.exerciseLearn');
          }
          if (this.$route.path.includes('speaking')) {
            title = this.$t('app.page.title.exerciseSpeaking');
          }
          if (this.$route.path.includes('grammar')) {
            title = this.$t('app.page.title.exerciseGrammar');
          }
          if (this.$route.path.includes('saved')) {
            title = this.$t('app.page.title.exerciseSaved');
          }
          break;
        case 'AssignmentsScreen':
          title = this.$t('app.page.title.assignmentDetails');
          break;
        case 'AssignmentStudentScreen':
          title = this.$t('app.page.title.assignmentDetails');
          break;
        case 'BundleItemScreen':
          title = this.$t('app.page.title.assignmentDetails');
          break;
        case 'BundleStudentScreen':
          title = this.$t('app.page.title.assignmentDetails');
          break;
        case 'AssignmentsScreenForStudent':
          title = this.$t('app.page.title.assignmentDetails');
          break;
        case 'AssignmentStudentScreenForStudent':
          title = this.$t('app.page.title.assignmentDetails');
          break;
        case 'BundleStudentScreenForStudent':
          title = this.$t('app.page.title.assignmentDetails');
          break;
        case 'PurchaseScreen':
          if (this.$route.path.includes('purchase/enquiry')) {
            title = this.$t('app.page.title.classOverview');
          }
          // eslint-disable-next-line max-len
          if (this.$route.path.includes('purchase/billing')) {
            title = this.$t('app.page.title.purchaseBilling');
          }
          // eslint-disable-next-line max-len
          if (this.$route.path.includes('purchase/payment')) {
            title = this.$t('app.page.title.purchasePayment');
          }
          // eslint-disable-next-line max-len
          if (this.$route.path.includes('purchase/success')) {
            title = this.$t('app.page.title.purchaseSuccess');
          }
          if (this.$route.path.includes('purchase/confirmed')) {
            title = this.$t('appapp.page.title.purchaseConfirmed');
          }
          if (this.$route.path === '/purchase') {
            title = this.$t('app.page.title.purchase');
          }
          break;
        case 'DKTAuthenticationScreen':
          title = this.$t('app.page.title.dktRedirects');
          break;
        case 'RedirectScreen':
          title = this.$t('app.page.title.dktRedirects');
          break;
        case 'DKTRedirectScreen':
          title = this.$t('app.page.title.dktRedirects');
          break;
        case 'PageNotFoundScreen':
          title = this.$t('app.page.title.pageNotFound');
          break;
        case 'PrivacyPolicy':
          title = this.$t('app.page.title.privacyPolicy');
          break;
        case 'TermsAndConditions':
          title = this.$t('app.page.title.termsAndConditions');
          break;
        case 'NotCompatibleScreen':
          title = this.$t('app.page.title.notCompatible');
          break;
        case 'UnsubscribedScreen':
          title = this.$t('app.page.title.unSubscribed');
          break;
        case 'Coupons':
          title = this.$t('app.page.title.coupons');
          break;
        case 'CouponDetails':
          title = this.$t('app.page.title.coupons');
          break;
        default:
          title = 'Xeropan Classroom';
      }
      document.title = title;
    }
  }
};
</script>

<style lang="scss">
@import '~>/css/global.scss';
#app {
  height: 100%;
  overflow-y: visible;

  &.disable-scroll {
    overflow: hidden;
    height: auto;
  }

  &.blurred {
    > :not(#modal):not(.mobile-suggester-screen) {
      filter: blur(5px);
      background-color: rgba(97, 97, 97, 0.9);
    }
    .IosBanner {
      filter: none !important;
      background-color: var(--white) !important;
    }
    .app-banner {
      filter: none !important;
      background-color: var(--white) !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue